// Las tres comprobaciones del expediente, antes de entregarlo.
//
// POR QUÉ EXISTE
//   Las tres ya corrían y ninguna se veía. Es exactamente el error que el
//   repositorio ya documentó una vez: «lo que hizo que nadie se diera cuenta no
//   fue la falta del dato —la bandera ya se guardaba— sino que no se mostraba
//   en ningún lado: un expediente con cifras de plantilla se veía idéntico a
//   uno derivado de verdad». Volvimos a construir tres detectores y los
//   dejamos en los logs y en la base.
//
// POR QUÉ NO BLOQUEA LA DESCARGA
//   Un hallazgo no es una condena. Un salario puede ser el del año anterior, un
//   DOI puede estar mal tipeado sobre una fuente real, y una Pro Forma de
//   plantilla puede ser aceptable para un borrador interno. Lo que no puede
//   pasar es que el operador entregue sin saberlo.
//
// POR QUÉ SE CALLA CUANDO ESTÁ TODO BIEN
//   Un panel que siempre dice algo se vuelve parte del fondo. Si las tres
//   comprobaciones pasan, muestra una línea y nada más.

import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, ChevronDown, ChevronRight, ShieldCheck } from 'lucide-react';

const CAIDAS = ['inexistente', 'mal_formada'];
const MARCADAS = ['otro_articulo', 'derogada', 'fuera_de_tema'];

// Una cita que llegó con `reemplazo` ya salió corregida en el documento: el
// identificador equivocado se sustituyó por el bueno antes de guardar. Pedirle
// al operador que la "revise" sería mandarlo a arreglar algo ya arreglado —
// exactamente la clase de aviso que le obliga a regenerar sin motivo.
const corregida = (v) => Boolean(v && v.reemplazo);

function resumirFuentes(sv) {
  const counts = (sv && sv.counts) || {};
  const verdicts = (sv && sv.verdicts) || [];
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const caidas = CAIDAS.reduce((n, k) => n + (counts[k] || 0), 0);
  const marcadas = verdicts.filter(
    (v) => MARCADAS.includes(v.status) && !corregida(v)).length;
  const corregidas = verdicts.filter(corregida).length;
  return { total, caidas, marcadas, corregidas, verificadas: counts.verificada || 0 };
}

export default function VerificacionDelExpediente({ plan }) {
  const [abierto, setAbierto] = useState(false);
  if (!plan) return null;

  const sv = plan.source_verification;
  const bls = plan.bls_discrepancias || [];
  const proc = plan.proforma_procedencia;
  const marcadores = plan.placeholders_pendientes || [];

  // Nada que mostrar: el documento es anterior a estas comprobaciones. Mejor no
  // decir nada que afirmar "todo bien" sobre algo que nunca se comprobó.
  if (!sv && !proc && !bls.length && !marcadores.length) return null;

  const f = resumirFuentes(sv);
  const proformaMal = proc && proc.propia === false;
  const hallazgos = (f.caidas || 0) + (f.marcadas || 0) + bls.length
    + (proformaMal ? 1 : 0) + marcadores.length;

  if (hallazgos === 0) {
    return (
      <div className="mb-4 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-800">
        <ShieldCheck size={16} className="shrink-0" />
        <span>
          Verificado: {f.verificadas} cita{f.verificadas === 1 ? '' : 's'} comprobada
          {f.verificadas === 1 ? '' : 's'}
          {f.corregidas > 0 && `, ${f.corregidas} identificador${f.corregidas === 1 ? '' : 'es'} corregido${f.corregidas === 1 ? '' : 's'} solo${f.corregidas === 1 ? '' : 's'}`}
          , salarios del BLS sin discrepancias
          {proc ? `, Pro Forma ${proc.etiqueta?.toLowerCase()}` : ''}.
        </span>
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50">
      <button
        type="button"
        onClick={() => setAbierto(!abierto)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        <AlertTriangle size={20} className="shrink-0 text-amber-600" />
        <span className="flex-1 font-semibold text-amber-900">
          Revisar antes de entregar — {hallazgos} hallazgo{hallazgos === 1 ? '' : 's'}
        </span>
        {abierto ? <ChevronDown size={18} className="text-amber-700" />
                 : <ChevronRight size={18} className="text-amber-700" />}
      </button>

      {abierto && (
        <div className="space-y-4 border-t border-amber-200 px-4 py-3 text-sm text-slate-800">

          {marcadores.length > 0 && (
            <div>
              <div className="font-semibold text-slate-900">
                Marcadores de plantilla sin rellenar
              </div>
              <p className="mt-1 text-slate-700">
                Esto se queda escrito tal cual en el PDF y en el Word. No se
                rellena solo: son datos del expediente.
              </p>
              <ul className="mt-1 space-y-1">
                {marcadores.slice(0, 10).map((m, i) => (
                  <li key={i} className="text-slate-700">
                    <code className="rounded bg-white px-1 text-xs">{m.marcador}</code>{' '}
                    — {m.apariciones} {m.apariciones === 1 ? 'vez' : 'veces'}
                    {m.secciones?.length
                      ? `, en la${m.secciones.length === 1 ? '' : 's'} sección${m.secciones.length === 1 ? '' : 'es'} ${m.secciones.join(', ')}`
                      : ''}
                    {m.ejemplo ? <span className="block text-xs text-slate-500">«…{m.ejemplo}…»</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {proformaMal && (
            <div>
              <div className="font-semibold text-slate-900">Cifras financieras</div>
              <p className="mt-1 text-slate-700">
                {proc.etiqueta}
                {proc.aviso ? ` — ${proc.aviso}` : ''}
              </p>
            </div>
          )}

          {bls.length > 0 && (
            <div>
              <div className="font-semibold text-slate-900">
                Salarios que no coinciden con el BLS
              </div>
              <ul className="mt-1 space-y-1">
                {bls.map((h, i) => (
                  <li key={i} className="text-slate-700">
                    El documento cita <strong>${h.citado?.toLocaleString()}</strong> para{' '}
                    {h.ocupacion}; el BLS publica{' '}
                    <strong>${h.real?.toLocaleString()}</strong>.
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(f.caidas > 0 || f.marcadas > 0) && (
            <div>
              <div className="font-semibold text-slate-900">Fuentes citadas</div>
              <p className="mt-1 text-slate-700">
                {f.verificadas} comprobada{f.verificadas === 1 ? '' : 's'}
                {f.caidas > 0 && `, ${f.caidas} no existe${f.caidas === 1 ? '' : 'n'} y se quitaron`}
                {f.marcadas > 0 && `, ${f.marcadas} marcada${f.marcadas === 1 ? '' : 's'} para revisar`}.
              </p>
              <ul className="mt-1 space-y-1">
                {(sv.verdicts || [])
                  .filter((v) => (CAIDAS.includes(v.status) || MARCADAS.includes(v.status))
                    && !corregida(v))
                  .slice(0, 12)
                  .map((v, i) => (
                    <li key={i} className="text-slate-700">
                      <code className="rounded bg-white px-1 text-xs">{v.ident}</code>{' '}
                      — {v.detail}
                    </li>
                  ))}
              </ul>
            </div>
          )}

          <p className="border-t border-amber-200 pt-2 text-xs text-slate-600">
            Nada de esto bloquea la descarga. Un salario puede ser el del año
            anterior y un DOI puede estar mal tecleado sobre una fuente real: lo
            decide quien conoce el expediente.
          </p>
        </div>
      )}
    </div>
  );
}

export { VerificacionDelExpediente };
